import { consume } from '@lit/context';
import { html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ColorCollapsibleDto, ColorCollapsibleStepDto } from '@/api-sdk';
import { global, GlobalComponentType } from '@/context';
import { isScrolledIntoView, scrollStepIntoView } from '@/utils';

import '@/components/accordion/fg-accordion';
import '@/components/accordion/fg-accordion-footer';
import '@/components/accordion/fg-accordion-title';
import '@/components/configurator/step-collapsible/color/fg-collapsible-color';

@customElement('fg-color-stacked')
export class FgColorStacked extends LitElement {
  @property({ type: Object })
  stepData?: ColorCollapsibleStepDto;

  @property({ type: Number })
  stepIndex!: number;

  @property({ type: Boolean })
  scrollToStep = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
      scrollStepIntoView(this);
      isScrolledIntoView(this);
    }
  }

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  render() {
    if (this.stepData === undefined) return nothing;
    const { layout } = this.globalContext;
    const colorSelections = this.stepData?.colorSelections;
    return html`
      <fg-accordion .stepData=${this.stepData}>
        ${colorSelections?.map(
          (colorSelection: ColorCollapsibleDto, index: number) => {
            return html`
              <fg-accordion-wrapper slot="accordion-content">
                <fg-accordion-title
                  part=${layout}
                  slot="accordion-wrapper-header"
                  index=${index}
                  title=${colorSelection.title}
                  .stepType=${this.stepData?.type}
                  .a11yTitle=${colorSelection.title}
                  .description=${{
                    selectedOption: colorSelection.selectedColor ?? undefined,
                  }}
                  .editQueryString=${colorSelection.editQueryString}
                  .removeQueryString=${colorSelection.removeQueryString}
                  .cancelQueryString=${colorSelection.cancelQueryString}
                  .collapseQueryString=${colorSelection.collapseQueryString}
                  .open=${colorSelection.open}
                  .valid=${colorSelection.selectedColor !== null}
                  @action-edit=${() =>
                    this.updateComponent(colorSelection.editQueryString)}
                  @action-delete=${() => {
                    this.updateComponent(colorSelection.removeQueryString);
                  }}
                ></fg-accordion-title>
                ${colorSelection.open && colorSelection.colors.length > 0
                  ? html`
                      <fg-collapsible-color
                        slot="accordion-wrapper-body"
                        stepIndex=${this.stepIndex}
                        title=${colorSelection.title}
                        colorItemIndex=${index}
                        .colors=${colorSelection.colors}
                        .open=${colorSelection.open}
                      ></fg-collapsible-color>
                    `
                  : nothing}
              </fg-accordion-wrapper>
              ${index + 1 === colorSelections.length &&
              colorSelections[index].selectedColor !== null &&
              !colorSelections[index].open
                ? html`
                    <fg-accordion-footer
                      slot="accordion-footer"
                      index=${index}
                      .queryString=${this.stepData?.addColorQueryString}
                      buttonText="actions.chooseExtraColor"
                    ></fg-accordion-footer>
                  `
                : nothing}
            `;
          },
        )}
      </fg-accordion>
    `;
  }
}
